import {Renderer2} from "@angular/core";
import {hasElements, Utils} from "./generics.utils";
import {License, LicenseRegexCountry} from "../../core/models/vehicle";
import {ScannerResult} from "../../core/models/scanner-results";
import {DialogErrorData} from "../components/dialog-error/models/dialog-error";
import isEmpty from "lodash/isEmpty";
import {registerLocaleData} from "@angular/common";
import {Company} from "../../core/models/company";
import * as Sentry from "@sentry/angular";
import {CdsLocalizedInputValue} from "@carool1/ngx-carool-ds";

export class UiUtils {

  public static readonly FALLBACK_LANG = 'en';

  public static readonly SUPPORTED_LOCALE_MAP: Record<string, () => Promise<any>> = {
    fr: () => import('@angular/common/locales/fr').then(m => m.default),
    de: () => import('@angular/common/locales/de').then(m => m.default),
    en: () => import('@angular/common/locales/en-GB').then(m => m.default),
    it: () => import('@angular/common/locales/it').then(m => m.default),
    es: () => import('@angular/common/locales/es').then(m => m.default),
    ar: () => import('@angular/common/locales/ar').then(m => m.default),
    nl: () => import('@angular/common/locales/nl').then(m => m.default),
    ko: () => import('@angular/common/locales/ko').then(m => m.default),
    'en-US': () => import('@angular/common/locales/en').then(m => m.default),
  };

  static async registerLocale(lang: string): Promise<void> {
    const loader = this.SUPPORTED_LOCALE_MAP[lang] ?? this.SUPPORTED_LOCALE_MAP[this.FALLBACK_LANG];
    const locale = await loader();
    registerLocaleData(locale);
  }

  /**
   * @deprecated Should use the version in license.utils.
   */
  public static getLicenseWithSeparator(license: License|undefined, acceptedLicenses: LicenseRegexCountry[]) {
    let fLicense = license?.license;
    if (license?.countryISO) {
      const matchedRegex = acceptedLicenses.find(al => al.countryCode === license.countryISO && al.regex.test(license.license));
      if (matchedRegex) {
        if (hasElements(matchedRegex.separators)) {
          fLicense = UiUtils.formatLicensePlate(matchedRegex, matchedRegex.separators!, license.license);
        } else {
          let separators = matchedRegex.regex.toString().split(/\(.*?\)/).slice(1);
          separators.pop();
          separators = separators.map((s) => s.includes('\s') ? ' ' : s.replace('{0,1}', ''));

          fLicense = UiUtils.formatLicensePlate(matchedRegex, separators, license.license);
        }
      }
    }
    return fLicense?.toUpperCase();
  }

  /**
   * @deprecated Should use the version in license.utils.
   */
  private static formatLicensePlate(regex: LicenseRegexCountry, separators: string[], fLicense: string) {
    const result = regex.regex.exec(fLicense)?.filter(v => !isEmpty(v)); // Get all groups for the license from the executed regex.
    if (result) {
      fLicense = '';
      if (result.length <= 2) { // If result length is under two, there is no group, so no separator to apply.
        fLicense = result[0];
      } else { // Ignore first value as it is initial text and then concatenate regex group and separators.
        result.slice(1).forEach((group, i) => {
          fLicense += group
          if (i < separators.length) {
            fLicense += separators[i]
          }
        })
      }
    }

    return fLicense;
  }

  public static getOrientationAngle() {
    return Utils.isOS() ? window.orientation : window.screen.orientation.angle;
  }

  public static isLandscape() {
    const angle = Utils.isOS() ? Math.abs(window.orientation) : window.screen.orientation.angle;
    return angle === 90 || angle === 270;
  }

  public static manageRotation(renderer: Renderer2) {
    if (UiUtils.isLandscape()) {
      renderer.addClass(document.body.parentElement, 'rotate');
    } else {
      renderer.removeClass(document.body.parentElement, 'rotate');
    }
  }

  public static getBrowserLanguage(): string {
    return UiUtils.formatLocale(navigator.language);
  }

  public static getBrowserCountry() {
    const country = UiUtils.getBrowserLanguage();
    const split = country.split('-');
    return split[split.length - 1].toLowerCase();
  }

  public static formatLocale(locale: string)  {
    const lang = locale.replace('_', '-');
    const parts = lang.split('-');
    if (parts.length === 1) {
      return parts[0].toLowerCase();
    }

    const language = parts[0].toLowerCase();
    const country = parts[parts.length - 1].substring(0, 2).toUpperCase();
    return `${language}-${country}`;
  }

  public static getFormattedScannerResult(result?: ScannerResult) {
    if(!result) {
      return '';
    }

    let value: string = `${result.width}/${result.ratio} ${result.isZr ? 'ZR' : 'R'}${result.diameter} ${result.loadIndex}${result.speedIndex}`;
    return value.replaceAll('null', '_');
  }

  public static getNoNetworkDialog() {
    const data = new DialogErrorData();
    data.icon = 'wifi_off';
    data.title = 'error.no-network.title';
    data.button = 'error.no-network.button';
    data.actionCallback = () => {
      window.location.reload();
    };
    return data;
  }

}

export const getSupportedLanguage = (locale = UiUtils.getBrowserLanguage()) => {
  let supportedLanguage: string;
  if (UiUtils.SUPPORTED_LOCALE_MAP[locale]) {
    supportedLanguage = locale;
  } else if (UiUtils.SUPPORTED_LOCALE_MAP[locale.substring(0, 2)]) {
    supportedLanguage = locale.substring(0, 2);
  } else {
    supportedLanguage = UiUtils.FALLBACK_LANG;
  }
  return supportedLanguage
}

export const isMobile = ()=> {
  const userAgent = navigator.userAgent.toLowerCase();
  return /iphone|ipod|android.*mobile|windows phone/.test(userAgent) || window.innerWidth < 768;
}

export const isTablet = ()=> {
  const userAgent = navigator.userAgent.toLowerCase();
  return /ipad|android(?!.*mobile)/.test(userAgent) || (window.innerWidth >= 768 && window.innerWidth <= 1024);
}

export const isDesktop = ()=> {
  return !isMobile() && !isTablet();
}

export const getDeviceType = ()=> {
  let isMobileOrTablet;
  // User agent data still experimental so we need to cast like this.
  const userAgentData = (navigator as any).userAgentData;
  if (userAgentData) {
    isMobileOrTablet = userAgentData.mobile;
  }
  if (isMobileOrTablet) {
    return isMobile() ? DeviceType.MOBILE : DeviceType.TABLET;
  }
  // If we're considered as desktop but we resized window we wants to be marked as
  // tablet or mobile.
  return isDesktop() ? DeviceType.DESKTOP : isMobile() ? DeviceType.MOBILE : DeviceType.TABLET;
}

export const getSupportedPhoneValues = (company: Company): CdsLocalizedInputValue[] | undefined  => {
  const countryInfo = company.countryInfo;
  return company.phoneAuthorizedCountries?.map((acceptedPhone) => {
    const country = countryInfo?.find((c) => c.countryCode === acceptedPhone);
    if (!country){
      Sentry.captureException(new Error(`Configuration error: no matching country found`), {extra: {acceptedPhone}});
      return undefined;
    }
    return {
      localizedName: country.countryName,
      countryISO: country.countryCode,
      flagUrl: country.countryFlagUrl
    };
  }).filter(Boolean) as (CdsLocalizedInputValue[] | undefined);
}

export enum DeviceType {
  MOBILE,
  TABLET,
  DESKTOP
}
